import React, { useState } from "react";
import axios from "axios";
import { Form, Modal } from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../config/translations";
import Button from "../../components/ui/Button";
import Toast from "../../components/ui/Toast";
import './loginb.scss';
import './LoginImproved.scss';

function LoginImproved({ language = 'hu' }) {
    const { loginState, userName, login, logout, token } = useAuth();
    const { t } = useTranslation(language);

    const [showModal, setShowModal] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [isPasswordChanging, setIsPasswordChanging] = useState(false);

    const handleOpenModal = () => {
        setShowModal(true);
        setEmail('');
        setPassword('');
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEmail('');
        setPassword('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            Toast.error(t('messages.invalidData'));
            return;
        }

        setIsLoading(true);
        const toastId = Toast.loading(t('auth.login') + '...');

        try {
            const result = await login({ login: email, password });

            Toast.dismiss(toastId);

            if (result.success) {
                Toast.success('Sikeres bejelentkezés!');
                handleCloseModal();
            } else {
                Toast.error(result.error || 'Bejelentkezési hiba');
            }
        } catch (error) {
            Toast.dismiss(toastId);
            Toast.error('Hiba történt a bejelentkezés során');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        // Toast.info('Kijelentkezve');
    };

    const handleOpenPasswordModal = () => {
        setShowPasswordModal(true);
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');
    };

    const handleClosePasswordModal = () => {
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (!currentPassword || !newPassword || !newPasswordConfirm) {
            Toast.error(t('messages.invalidData'));
            return;
        }
        if (newPassword !== newPasswordConfirm) {
            Toast.error(t('auth.passwordMismatch'));
            return;
        }

        setIsPasswordChanging(true);
        const toastId = Toast.loading(t('auth.changePassword') + '...');

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/users/change-password`,
                {},
                {
                    headers: {
                        token,
                        currentpassword: currentPassword,
                        newpassword: newPassword,
                    },
                }
            );

            Toast.dismiss(toastId);

            if (response.data.httpCode === 200) {
                Toast.success(t('auth.passwordChanged'));
                handleClosePasswordModal();
            } else {
                Toast.error(response.data.httpMessage || t('messages.error'));
            }
        } catch (error) {
            Toast.dismiss(toastId);
            Toast.error(error.response?.data?.message || t('messages.error'));
        } finally {
            setIsPasswordChanging(false);
        }
    };

    if (loginState === 'LOGGEDIN') {
        return (
            <div className="login-wrapper">
                <div className="user-info">
                    <span className="user-email">{userName}</span>
                </div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleOpenPasswordModal}
                >
                    {t('auth.changePassword')}
                </Button>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleLogout}
                >
                    {t('auth.logout')}
                </Button>

                <Modal show={showPasswordModal} onHide={handleClosePasswordModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('auth.changePassword')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleChangePassword}>
                            <Form.Group className="mb-3" controlId="formCurrentPassword">
                                <Form.Label>{t('auth.currentPassword')}</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    disabled={isPasswordChanging}
                                    autoFocus
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="formNewPassword">
                                <Form.Label>{t('auth.newPassword')}</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={isPasswordChanging}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="formNewPasswordConfirm">
                                <Form.Label>{t('auth.newPasswordConfirm')}</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={newPasswordConfirm}
                                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                                    disabled={isPasswordChanging}
                                />
                            </Form.Group>

                            <div className="d-flex gap-2 justify-content-end">
                                <Button
                                    variant="secondary"
                                    onClick={handleClosePasswordModal}
                                    disabled={isPasswordChanging}
                                >
                                    {t('auth.cancel')}
                                </Button>
                                <Button
                                    variant="success"
                                    type="submit"
                                    loading={isPasswordChanging}
                                    disabled={isPasswordChanging}
                                >
                                    {t('auth.submit')}
                                </Button>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>
            </div>
        );
    }

    return (
        <>
            <Button
                variant="primary"
                size="sm"
                onClick={handleOpenModal}
            >
                {t('auth.login')}
            </Button>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('auth.login')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="formEmail">
                            <Form.Label>{t('auth.email')}</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder={t('auth.email')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                                autoFocus
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formPassword">
                            <Form.Label>{t('auth.password')}</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder={t('auth.password')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                            />
                        </Form.Group>

                        <div className="d-flex gap-2 justify-content-end">
                            <Button
                                variant="secondary"
                                onClick={handleCloseModal}
                                disabled={isLoading}
                            >
                                {t('auth.cancel')}
                            </Button>
                            <Button
                                variant="success"
                                type="submit"
                                loading={isLoading}
                                disabled={isLoading}
                            >
                                {t('auth.submit')}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default LoginImproved;
